import type { TReaderDocument } from '@app-evolution-cms/email-builder';
import { ChevronDownIcon, ChevronRightIcon } from '@radix-ui/react-icons';
import { useEffect, useMemo, useRef, useState } from 'react';

import { Button } from '../../../../primitives/button';
import { FolderTreeIcon } from '../../../../svg/folder-tree-icon';
import { cn } from '../../../../utils/cn';
import { BLOCK_ICONS } from '../constants/block-icons';

type TreeNode = {
  id: string;
  blockId?: string;
  label: string;
  icon: (typeof BLOCK_ICONS)[keyof typeof BLOCK_ICONS];
  children: TreeNode[];
};

const BLOCK_LABELS = {
  EmailLayout: 'Global',
  Container: 'Contenedor',
  ColumnsContainer: 'Columnas',
  Heading: 'Encabezado',
  Text: 'Texto',
  Button: 'Botón',
  Image: 'Imagen',
  Avatar: 'Avatar',
  Divider: 'Divisor',
  Spacer: 'Espaciador',
  Html: 'HTML',
} satisfies Record<TReaderDocument[string]['type'], string>;

function buildTree(document: TReaderDocument, blockIds: string[]): TreeNode[] {
  return blockIds.flatMap((blockId) => {
    const block = document[blockId];
    if (!block) return [];

    let children: TreeNode[] = [];
    if (block.type === 'EmailLayout') {
      children = buildTree(document, block.data.childrenIds ?? []);
    } else if (block.type === 'Container') {
      children = buildTree(document, block.data.props?.childrenIds ?? []);
    } else if (block.type === 'ColumnsContainer') {
      children = Array.from({ length: block.data.props?.columnsCount ?? 2 }, (_, index) => ({
        id: `column:${blockId}:${index}`,
        label: `Columna ${index + 1}`,
        icon: BLOCK_ICONS.ColumnsContainer,
        children: buildTree(document, block.data.props?.columns?.[index]?.childrenIds ?? []),
      }));
    }

    return [
      {
        id: `block:${blockId}`,
        blockId,
        label: BLOCK_LABELS[block.type],
        icon: BLOCK_ICONS[block.type],
        children,
      },
    ];
  });
}

function flattenTree(
  nodes: TreeNode[],
  collapsedIds: Set<string>,
  ancestors: string[] = [],
): { node: TreeNode; ancestors: string[]; position: number; size: number }[] {
  return nodes.flatMap((node, index) => [
    { node, ancestors, position: index + 1, size: nodes.length },
    ...(collapsedIds.has(node.id)
      ? []
      : flattenTree(node.children, collapsedIds, [...ancestors, node.id])),
  ]);
}

type ElementTreeProps = {
  document: TReaderDocument;
  rootBlockId: string;
  selectedBlockId: string | null;
  onSelect: (blockId: string) => void;
};

export function ElementTree({
  document,
  rootBlockId,
  selectedBlockId,
  onSelect,
}: ElementTreeProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [collapsedIds, setCollapsedIds] = useState<Set<string>>(new Set());
  const [focusedId, setFocusedId] = useState<string | null>(null);
  const itemRefs = useRef(new Map<string, HTMLDivElement>());
  const nodes = useMemo(() => buildTree(document, [rootBlockId]), [document, rootBlockId]);
  const allItems = useMemo(() => flattenTree(nodes, new Set()), [nodes]);
  const visibleItems = flattenTree(nodes, collapsedIds);
  const tabStopId = visibleItems.some(({ node }) => node.id === focusedId)
    ? focusedId
    : (visibleItems.find(({ node }) => node.blockId === selectedBlockId)?.node.id ??
      visibleItems[0]?.node.id);

  useEffect(() => {
    const selectedItem = allItems.find(({ node }) => node.blockId === selectedBlockId);
    if (!selectedItem) return;

    setCollapsedIds((current) => {
      if (!selectedItem.ancestors.some((id) => current.has(id))) return current;
      const next = new Set(current);
      selectedItem.ancestors.forEach((id) => next.delete(id));
      return next;
    });
  }, [allItems, selectedBlockId]);

  const toggle = (id: string) => {
    setCollapsedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const focusItem = (id: string | undefined) => {
    if (id) itemRefs.current.get(id)?.focus();
  };

  return (
    <aside
      aria-label="Estructura del email"
      className={cn(
        'sticky top-6 flex max-h-[calc(90svh-3rem)] shrink-0 translate-x-0 flex-col self-start overflow-hidden border bg-card opacity-100 transition-[width,opacity,transform,translate,scale,rotate] duration-300 ease-in-out',
        isOpen ? 'w-60 rounded-lg' : 'w-[3.9rem] rounded-full',
      )}
    >
      <div className="flex items-center gap-4 p-3">
        <Button
          title={isOpen ? 'Cerrar panel de elementos' : 'Abrir panel de elementos'}
          aria-label={isOpen ? 'Cerrar panel de elementos' : 'Abrir panel de elementos'}
          aria-expanded={isOpen}
          className="shrink-0"
          size="icon"
          variant="secondary"
          onClick={() => setIsOpen((open) => !open)}
        >
          <FolderTreeIcon />
        </Button>
        {isOpen && <span className="text-sm font-semibold whitespace-nowrap">Elementos</span>}
      </div>
      {isOpen && (
        <div role="tree" aria-label="Elementos del email" className="overflow-auto p-2">
          {visibleItems.map(({ node, ancestors, position, size }, index) => {
            const isExpanded = node.children.length > 0 && !collapsedIds.has(node.id);
            return (
              <div
                key={node.id}
                ref={(element) => {
                  if (element) itemRefs.current.set(node.id, element);
                  else itemRefs.current.delete(node.id);
                }}
                role="treeitem"
                aria-label={node.label}
                aria-level={ancestors.length + 1}
                aria-posinset={position}
                aria-setsize={size}
                aria-selected={node.blockId ? node.blockId === selectedBlockId : undefined}
                aria-expanded={node.children.length > 0 ? isExpanded : undefined}
                tabIndex={node.id === tabStopId ? 0 : -1}
                style={{ paddingLeft: ancestors.length * 16 + 4 }}
                className={cn(
                  'flex cursor-pointer items-center gap-1 rounded-md py-1 text-sm outline-none hover:bg-secondary focus-visible:ring-2 focus-visible:ring-ring',
                  node.blockId === selectedBlockId && 'bg-accent text-accent-foreground',
                )}
                onFocus={() => setFocusedId(node.id)}
                onClick={(event) => {
                  event.currentTarget.focus();
                  if (node.blockId) onSelect(node.blockId);
                  else toggle(node.id);
                }}
                onKeyDown={(event) => {
                  if (event.target !== event.currentTarget) return;
                  switch (event.key) {
                    case 'ArrowDown':
                      focusItem(visibleItems[index + 1]?.node.id);
                      break;
                    case 'ArrowUp':
                      focusItem(visibleItems[index - 1]?.node.id);
                      break;
                    case 'ArrowRight':
                      if (node.children.length > 0) {
                        if (!isExpanded) toggle(node.id);
                        else focusItem(visibleItems[index + 1]?.node.id);
                      }
                      break;
                    case 'ArrowLeft':
                      if (isExpanded) toggle(node.id);
                      else focusItem(ancestors[ancestors.length - 1]);
                      break;
                    case 'Home':
                      focusItem(visibleItems[0]?.node.id);
                      break;
                    case 'End':
                      focusItem(visibleItems[visibleItems.length - 1]?.node.id);
                      break;
                    case 'Enter':
                    case ' ':
                      if (node.blockId) onSelect(node.blockId);
                      else toggle(node.id);
                      break;
                    default:
                      return;
                  }
                  event.preventDefault();
                }}
              >
                {node.children.length > 0 ? (
                  <Button
                    aria-label={`${isExpanded ? 'Colapsar' : 'Expandir'} ${node.label}`}
                    tabIndex={-1}
                    className="size-6! shrink-0"
                    size="icon-sm"
                    variant="ghost"
                    onClick={(event) => {
                      event.stopPropagation();
                      focusItem(node.id);
                      toggle(node.id);
                    }}
                  >
                    {isExpanded ? <ChevronDownIcon /> : <ChevronRightIcon />}
                  </Button>
                ) : (
                  <span className="w-6 shrink-0" />
                )}
                <node.icon aria-hidden="true" className="shrink-0" />
                <span className="truncate">{node.label}</span>
              </div>
            );
          })}
        </div>
      )}
    </aside>
  );
}
